import type { SupabaseClient } from '@supabase/supabase-js';
import { awaitJob } from '../../lib/await-job.js';

// ============================================================================
// Otto-Lister — Bestand · Lager · Min/Max (eigener Tab „Bestand & Lager", vor Vorschau).
//
// Lager als MEHRFACHAUSWAHL: Bestand = Summe der verfügbaren Bestände über alle markierten
// Lager (View otto.listing_stock_resolved / Worker-Fetch). Einzelartikel → wirkt auf die SKU;
// Variantenvater → setzt den DEFAULT (Lager/Min/Max), den Kinder ohne eigenen Wert erben
// (per-Kind-Override im Varianten-Tab). Bestand read-only. Speichern über die Per-Tab-Registry.
// Styling: go-eCommerce-Design-System (kl-sec-lab / kl-sec-note / otto-mfield / v-input).
// ============================================================================

interface WarehouseRow { id: string; number?: string | null; displayName?: string | null; salesStorage?: boolean }
interface StockRow {
  effective_storage_ids: string[] | null;
  own_storage_ids: string[] | null;
  effective_min: number | null; effective_max: number | null;
  own_min: number | null; own_max: number | null;
  own_default_min: number | null; own_default_max: number | null;
  storefront_storage_id: string | null; storefront_storage_label: string | null;
  vario_stock: number | string | null; vario_product_type: string | null;
}

export interface StockBlockCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
  registerSaver?: (id: string, fn: () => Promise<void>) => void;
}
export interface StockBlockArgs {
  listingId: string;
  isVariantParent: boolean;
  sku: string | null;            // vario_article_number
}

export async function renderStockBlock(host: HTMLElement, ctx: StockBlockCtx, args: StockBlockArgs): Promise<void> {
  host.innerHTML = `<div class="v-pa-md"><span class="v-text-muted" style="font-size:12px;"><span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade Bestand &amp; Lager…</span></div>`;

  let warehouses: WarehouseRow[] = [];
  let row: StockRow | null = null;
  try {
    const [whRes, stockRes] = await Promise.all([
      fetch(`${ctx.supabaseUrl.replace(/\/$/, '')}/functions/v1/vario-warehouses-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
        body: JSON.stringify({ marketplace: 'otto' }),
      }).then((r) => r.json().catch(() => ({}))).catch(() => ({})),
      ctx.supabase.rpc('otto_get_listing_stock', { _listing_id: args.listingId }),
    ]);
    warehouses = ((whRes as { warehouses?: WarehouseRow[] })?.warehouses ?? []);
    const data = stockRes.data as StockRow[] | StockRow | null;
    row = Array.isArray(data) ? (data[0] ?? null) : data;
  } catch (e) {
    host.innerHTML = banner('error', `Bestand/Lager nicht ladbar: ${(e as Error).message}`);
    return;
  }

  const parent = args.isVariantParent;
  const curMin = parent ? row?.own_default_min ?? null : row?.own_min ?? null;
  const curMax = parent ? row?.own_default_max ?? null : row?.own_max ?? null;
  const effMin = row?.effective_min ?? null;
  const effMax = row?.effective_max ?? null;
  // Vorauswahl: eigene Auswahl des Listings, sonst die EFFEKTIVEN Lager (= in den Otto-Einstellungen
  // bzw. am Vater definierten). So sind die Einstellungs-Lager im Listing immer vorausgewählt.
  const ownArr = row?.own_storage_ids ?? [];
  const preIds = new Set(ownArr.length > 0 ? ownArr : (row?.effective_storage_ids ?? []));
  const sfLabel = row?.storefront_storage_label ?? null;
  const stockVal = row?.vario_stock == null ? null : Number(row.vario_stock);

  const whChecks = warehouses.length === 0
    ? `<div class="v-text-muted" style="font-size:12px;">Keine Vario-Lager gefunden — bitte in den Einstellungen prüfen.</div>`
    : warehouses.map((w) => {
        const label = w.displayName || w.number || w.id;
        const checked = preIds.has(w.id) ? ' checked' : '';
        return `<label class="otto-wh-opt">
          <input type="checkbox" class="otto-wh-cb" value="${escapeAttr(w.id)}" data-label="${escapeAttr(label)}"${checked} />
          <span>${escapeHtml(label)}${w.salesStorage ? ' <span class="v-text-muted">· Verkauf</span>' : ''}</span>
        </label>`;
      }).join('');

  const mfield = (label: string, control: string): string =>
    `<div class="otto-mfield"><div class="otto-mfield-label"><div class="kl-mf-label">${escapeHtml(label)}</div></div><div class="otto-mfield-ctl">${control}</div></div>`;

  host.innerHTML = `
    <div class="v-pa-md">
      <div class="kl-sec-lab">Lager${parent ? ' · Vater-Default' : ''}</div>
      <div class="kl-sec-note">
        Mehrfachauswahl möglich — der Bestand ist die <b>Summe</b> der verfügbaren Bestände über alle markierten Lager.
        Keine Auswahl ⇒ Standard-Lager aus den Einstellungen${sfLabel ? ` (${escapeHtml(sfLabel)})` : ''}.${parent ? ' Gilt als Default für alle Varianten (je Kind im Varianten-Tab überschreibbar).' : ''}
      </div>
      <div class="otto-wh-list" style="display:flex;flex-wrap:wrap;gap:6px 16px;margin:6px 0 14px;">${whChecks}</div>

      <div class="kl-sec-lab">Bestand &amp; Min/Max</div>
      <div class="kl-sec-note">Ohne Min/Max wird der aktuell verfügbare Bestand gesendet.</div>
      ${mfield('Verfügbarer Bestand', `<input class="v-input" id="otto-stock-avail" type="text" style="max-width:160px;" value="${parent ? '— je Variante —' : (stockVal == null ? '—' : String(stockVal))}" readonly title="Aktuell verfügbarer Vario-Bestand (Summe der markierten Lager). Wird beim Speichern neu ermittelt." />`)}
      ${mfield('Min Bestand', `<input class="v-input" id="otto-stock-min" type="number" inputmode="numeric" min="0" step="1" style="max-width:160px;" value="${curMin ?? ''}" placeholder="${effMin != null && curMin == null ? `geerbt: ${effMin}` : ''}" />`)}
      ${mfield('Max Bestand', `<input class="v-input" id="otto-stock-max" type="number" inputmode="numeric" min="0" step="1" style="max-width:160px;" value="${curMax ?? ''}" placeholder="${effMax != null && curMax == null ? `geerbt: ${effMax}` : ''}" />`)}
    </div>`;

  const save = async (): Promise<void> => {
    const ids = Array.from(host.querySelectorAll<HTMLInputElement>('.otto-wh-cb'))
      .filter((cb) => cb.checked)
      .map((cb) => cb.value);
    const storageIds = ids.length > 0 ? ids : null;
    const min = parseIntOrNull(host.querySelector<HTMLInputElement>('#otto-stock-min')?.value);
    const max = parseIntOrNull(host.querySelector<HTMLInputElement>('#otto-stock-max')?.value);

    if (parent) {
      const { error } = await ctx.supabase.rpc('otto_set_parent_stock_defaults', {
        _listing_id: args.listingId, _min: min, _max: max, _storage_ids: storageIds,
      });
      if (error) throw new Error(error.message);
    } else {
      // Min/Max listing-zentriert speichern (unabhängig von der Listing-SKU — Einzelartikel haben
      // oft keine vario_article_number): otto_set_listing_min_max_by_id löst die Mapping-Zeile wie
      // die View auf (Einzelartikel per vario_article_id, Kinder per SKU) und legt sie ggf. an.
      const mm = await ctx.supabase.rpc('otto_set_listing_min_max_by_id', {
        _listing_id: args.listingId, _min: min, _max: max,
      });
      if (mm.error) throw new Error(mm.error.message);
      const wh = await ctx.supabase.rpc('otto_set_listing_warehouse', {
        _listing_id: args.listingId, _storage_ids: storageIds,
      });
      if (wh.error) throw new Error(wh.error.message);
    }

    // Verfügbaren Bestand DIREKT ermitteln: gezielten Refresh-Job enqueuen und das Feld nach
    // Job-Ende aktualisieren (nur Einzelartikel — der Vater zeigt „je Variante"). Best-effort:
    // ein Fehler hier kippt das Speichern NICHT. Die geänderten Min/Max wirken beim nächsten
    // 10-Min-Push-Cron (bzw. sofort über „Aktualisieren") — kein event-getriebener Direkt-Push mehr.
    try {
      const { data: jobId } = await ctx.supabase.rpc('otto_enqueue_stock_refresh', { _listing_id: args.listingId });
      const availEl = host.querySelector<HTMLInputElement>('#otto-stock-avail');
      if (!parent && typeof jobId === 'string' && availEl) {
        availEl.value = 'wird ermittelt…';
        void awaitJob(ctx.supabase, jobId, { timeoutMs: 60_000, pollIntervalMs: 2_000 })
          .then(async () => {
            const { data } = await ctx.supabase.rpc('otto_get_listing_stock', { _listing_id: args.listingId });
            const r = (Array.isArray(data) ? data[0] : data) as StockRow | null;
            const v = r?.vario_stock == null ? null : Number(r.vario_stock);
            availEl.value = v == null ? '—' : String(v);
          })
          .catch(() => { availEl.value = stockVal == null ? '—' : String(stockVal); });
      }
    } catch { /* Refresh best-effort */ }
  };
  ctx.registerSaver?.('stock', save);
}

function parseIntOrNull(v: string | undefined | null): number | null {
  if (v == null) return null;
  const t = v.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function banner(kind: 'error', msg: string): string {
  return `<div class="v-pa-md"><div class="v-banner v-banner-danger"><span class="v-icon fa-light fa-circle-xmark"></span><div>${escapeHtml(msg)}</div></div></div>`;
}
function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}
function escapeAttr(s: string): string { return escapeHtml(s); }
